/**
 * Typed client for /admin-wallet/* — backs the Crypto Assets screen.
 *
 * The endpoints cover the four moving parts of NUREV's payout pipeline:
 *
 *   1. Wallet overview      — Monero node balance + outstanding balances +
 *                             active exchanges + 30-day status histogram
 *   2. Publisher balances   — who's owed what, paginated
 *   3. Withdrawals          — full withdrawal stream with status filter
 *                             + per-record live polling of tx + exchange
 *                             provider status
 *   4. Exchange execution   — admin-initiated XMR conversion via the
 *                             multi-provider router (ChangeNOW / Exolix /
 *                             StealthEX). Estimate first, then commit.
 *
 * No envelope on these endpoints — all return raw shapes from the
 * controller. Money is in two units depending on context:
 *   - USD (the native ledger unit)
 *   - XMR (6-decimal floats for display, piconeros server-side)
 */
import { client, unwrap } from './client';

export type WithdrawalStatus =
  | 'initiated'
  | 'transferring'
  | 'block confirming'
  | 'converting currency'
  | 'completed'
  | 'failed';

export interface WalletInfo {
  connected: boolean;
  balanceXmr?: number;
  height?: number;
}

export interface BalancesAggregate {
  totalBalance: number;
  totalBalanceXmr: number;
  publishersWithBalance: number;
}

export interface RecentByStatus {
  _id: WithdrawalStatus | string;
  count: number;
  totalFromAmount: number;
}

export interface ExchangesInfo {
  activeWithdrawals: number;
  providers: string[];
  recentByStatus: RecentByStatus[];
}

export interface WalletOverviewResponse {
  wallet: WalletInfo;
  balances: BalancesAggregate;
  exchanges: ExchangesInfo;
}

export interface PublisherBalanceRow {
  _id: string;
  publisherId: string;
  publisherName?: string;
  publisherEmail?: string;
  balance: number;
  balanceXmr: number;
  payoutCurrency?: string;
  payoutAddress?: string;
}

export interface PublisherBalanceListResponse {
  docs: PublisherBalanceRow[];
  totalDocs: number;
  totalPages: number;
  page: number;
  limit: number;
}

export interface AdminWithdrawalRow {
  _id: string;
  createdAt: string;
  publisherId?: string;
  publisherName?: string;
  status: WithdrawalStatus | string;
  fromAmount: number;
  fromCurrency: string;
  toAmount?: number;
  outCurrency?: string;
  exchangeProvider?: string;
  createdByAdmin?: boolean;
  txHash?: string;
}

export interface AdminWithdrawalListResponse {
  docs: AdminWithdrawalRow[];
  totalDocs: number;
  totalPages: number;
  page: number;
  limit: number;
}

export interface WithdrawalStatusResponse {
  withdrawal?: AdminWithdrawalRow;
  txStatus?: {
    status?: string;
    confirms?: number;
    error?: string;
  };
  exchangeStatus?: {
    status?: string;
    amount_to?: number;
    error?: string;
  };
}

export interface ExchangeEstimate {
  from: string;
  to: string;
  amount: number;
  estimate?: number;
  provider?: string;
}

export interface ExchangeCreateInput {
  address: string;
  currency: string;
  amountXmr: number;
}

export interface ExchangeCreateResponse {
  provider: string;
  txHash: string;
  exchangeId?: string;
}

// ─── Fetchers ─────────────────────────────────────────────────────────────

export async function fetchWalletOverview(): Promise<WalletOverviewResponse> {
  return unwrap<WalletOverviewResponse>(client.get('/admin-wallet/overview'));
}

export async function fetchPublisherBalances(params: {
  page?: number;
  limit?: number;
}): Promise<PublisherBalanceListResponse> {
  // NOTE: the legacy dashboard hook hit /admin-wallet/creator-balances,
  // but the actual server route is /publisher-balances. That made the
  // legacy Balances tab silently 404 in production (the UI just showed
  // empty rows). Caught during the admin.nurev.io port — fixed here.
  return unwrap<PublisherBalanceListResponse>(client.get(
    '/admin-wallet/publisher-balances',
    { params }
  ));
}

export async function fetchAdminWithdrawals(params: {
  page?: number;
  limit?: number;
  status?: WithdrawalStatus | '';
}): Promise<AdminWithdrawalListResponse> {
  const cleanParams: Record<string, unknown> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v !== '' && v !== undefined && v !== null) {
      cleanParams[k] = v;
    }
  });
  return unwrap<AdminWithdrawalListResponse>(client.get(
    '/admin-wallet/withdrawals',
    { params: cleanParams }
  ));
}

export async function fetchWithdrawalStatus(
  id: string
): Promise<WithdrawalStatusResponse> {
  return unwrap<WithdrawalStatusResponse>(client.get(
    `/admin-wallet/withdrawal/${id}/status`
  ));
}

export async function fetchExchangeCurrencies(): Promise<string[]> {
  return unwrap<string[]>(client.get('/admin-wallet/currencies'));
}

export async function fetchExchangeEstimate(params: {
  from: string;
  to: string;
  amount: number;
}): Promise<ExchangeEstimate> {
  return unwrap<ExchangeEstimate>(client.get('/admin-wallet/estimate', {
    params
  }));
}

export async function createExchange(
  input: ExchangeCreateInput
): Promise<ExchangeCreateResponse> {
  return unwrap<ExchangeCreateResponse>(client.post(
    '/admin-wallet/exchange',
    input
  ));
}
